import React, { createContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useImmer } from "use-immer";
import { produce } from "immer";
import { flowRight, template } from "lodash";
import axios from "axios";
import {
    Container,
    Header,
    Content,
    Footer as RsuiteFooter,
    Button,
    Checkbox,
    HStack,
    IconButton,
    Message,
    Progress,
    Stack,
    Table,
    Text,
    toaster,
    Heading,
    Whisper,
    Tooltip,
} from "rsuite";
import { SortType } from "rsuite-table";
import { TypeAttributes } from "rsuite/esm/internals/types";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";

import HelpOutlineIcon from "@rsuite/icons/HelpOutline";
import HistoryIcon from "@rsuite/icons/History";

import withLoader from "hocs/withLoader/withLoader";
import withError from "hocs/withError/withError";
import withWebSocket, {
    WithWebSocketProps,
} from "hocs/withWebSocket/withWebSocket";

import useRefreshPage from "hooks/useRefreshPage";
import useInactiveEnvironment from "hooks/useInactiveEnvironment";
import useRequestCancellation from "hooks/useRequestCancellation";

import constants from "utils/constants";
import i18n from "utils/i18n";
import { formatDateTimeShort } from "utils/date-helper";
import { getSorted } from "utils/sort-halper";
import { HUB_NAMES } from "utils/serverless-hub";

import { getErrorMessage } from "api/defaults";
import {
    createManagementPolicyRules,
    loadManagementPolicyRules,
    loadManagementPolicyRulesWorkflow,
    loadStorageAccounts,
} from "api/environments/storage-accounts-api";

import {
    ManagementPolicyRulesWorkflow,
    ManagementPolicyRulesWorkflowStep,
    WorkflowStatus,
    WorkflowStepStatus,
} from "store/workflows/workflows-types";
import { Tenant } from "store/environments/environment-types";
import {
    DateAfterModification,
    ManagementPolicyRule,
    StorageAccount,
} from "store/storage-accounts/storage-accounts-types";

import { AppDispatch, RootState } from "store/store";

import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import { fetchEnvironments } from "store/environments/environment-thunks";

import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";

import Footer from "components/Common/Footer/Footer";
import Collapsible from "components/Common/Collapsible/Collapsible";
import ConfirmModal from "components/Common/ConfirmModal/ConfirmModal";

import {
    getAppliedRuleValue,
    ruleNames,
    tierDescriptions,
} from "./storage-account-helpers";

import ErrorLogs from "./ErrorLogs/ErrorLogs";
import History from "./History/History";

import "./StorageAccounts.less";

interface Props extends WithWebSocketProps {
    title?: string;

    showLoader?: (loaderVisibility: boolean) => void;
    showError?: (error: string) => void;
}

type TableItem<T> = T & {
    checked: boolean;
    readonly: boolean;
};

const useAppDispatch = () => useDispatch<AppDispatch>();

export const HistoryContext = createContext<TableItem<
    Tenant<StorageAccount>
> | null>(null);

const StorageAccounts: React.FC<Props> = ({
    title = i18n.t("storageAccounts.title"),
    webSocketPayload,
    showLoader,
    showError,
    addWebSocketUserId,
    addWebSocketHabName,
}) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();

    const navigate = useNavigate();
    const params: Record<string, string> = useParams<{
        environmentId: string;
    }>();
    const { environmentId } = params;

    const dispatch = useAppDispatch();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);
    const [managementPolicyRulesWorkflow, setManagementPolicyRulesWorkflow] =
        useState<ManagementPolicyRulesWorkflow | null>(null);

    const [, setStorageAccounts] = useState<
        ReadonlyArray<Tenant<StorageAccount>>
    >([]);

    const [selectedStorageAccounts, setSelectedStorageAccounts] = useImmer<
        Tenant<StorageAccount>[]
    >([]);

    const [, setManagementPolicyRules] = useState<
        ReadonlyArray<ManagementPolicyRule>
    >([]);

    const [selectedManagementPolicyRules, setSelectedManagementPolicyRules] =
        useImmer<ManagementPolicyRule[]>([]);

    const [managementPolicyRulesTableData, setManagementPolicyRulesTableData] =
        useImmer<TableItem<ManagementPolicyRule>[]>([]);

    const [
        managementPolicyRulesAllChecked,
        setManagementPolicyRulesAllChecked,
    ] = useState<boolean>(true);

    const [storageAccountsTableData, setStorageAccountsTableData] = useImmer<
        TableItem<Tenant<StorageAccount>>[]
    >([]);

    const [
        selectedStorageAccountTableItem,
        setSelectedStorageAccountTableItem,
    ] = useState<TableItem<Tenant<StorageAccount>> | null>(null);
    const [storageAccountsSortColumn, setStorageAccountsSortColumn] =
        useState<keyof Tenant<StorageAccount>>();
    const [storageAccountsSortType, setStorageAccountsSortType] =
        useState<SortType>();
    const [storageAccountsAllChecked, setStorageAccountsAllChecked] =
        useState<boolean>(false);

    const [errorLogsOpen, setErrorLogsOpen] = useState<boolean>(false);
    const [historyLogsOpen, setHistoryLogsOpen] = useState<boolean>(false);
    const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);

    const managementPolicyRulesIndeterminate =
        managementPolicyRulesTableData.some((ti) => !ti.checked);

    const storageAccountsIndeterminate = storageAccountsTableData.some(
        (ti) => !ti.checked
    );

    const headerHeight = 40;
    const rowHeight = 46;

    const primaryBlue = "#17a1e5";
    const primaryYellow = "#edba3c";

    const tostOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        setManagementPolicyRulesAllChecked(true);
        setStorageAccountsAllChecked(false);

        Promise.all([
            fetchStorageAccounts(),
            fetchManagementPolicyRules(),
            fetchManagementPolicyRulesWorkflow(),
        ])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchManagementPolicyRules = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadManagementPolicyRules(
                activeEnvironmentId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setManagementPolicyRules(payload);
                setManagementPolicyRulesTableData([
                    ...payload.map((sa) => ({
                        ...sa,
                        checked: true,
                        readonly: false,
                    })),
                ]);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchStorageAccounts = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadStorageAccounts(
                activeEnvironmentId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setStorageAccounts(payload);
                if (storageAccountsSortColumn && storageAccountsSortType) {
                    setStorageAccountsTableData(
                        getSorted(
                            [
                                ...payload.map((sa) => ({
                                    ...sa,
                                    checked: false,
                                    readonly: false,
                                })),
                            ],
                            storageAccountsSortColumn,
                            storageAccountsSortType
                        )
                    );
                } else {
                    setStorageAccountsTableData([
                        ...payload.map((sa) => ({
                            ...sa,
                            checked: false,
                            readonly: false,
                        })),
                    ]);
                }

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchManagementPolicyRulesWorkflow = async (): Promise<
        string | null
    > => {
        try {
            const [payload, status] = await loadManagementPolicyRulesWorkflow(
                activeEnvironmentId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setManagementPolicyRulesWorkflow(payload);

                return null;
            } else if (status === constants.statusCode.NotFound) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const submitManagementPolicyRules = async () => {
        if (selectedStorageAccounts.length === 0) {
            return null;
        }

        try {
            const [payload, status] = await createManagementPolicyRules(
                activeEnvironmentId,
                selectedStorageAccounts
            );
            if (status === constants.statusCode.OK && payload) {
                setManagementPolicyRulesWorkflow(payload);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const getCompletedStepsPrecent = (
        steps: ReadonlyArray<ManagementPolicyRulesWorkflowStep> | undefined
    ): number => {
        if (steps) {
            const all = steps.length;
            const completed = steps.filter(
                (s) =>
                    s.status === WorkflowStepStatus.Completed ||
                    s.status === WorkflowStepStatus.Failed
            ).length;

            return (completed / all) * 100;
        }

        return 0;
    };

    const getProgressStatement = (
        steps: ReadonlyArray<ManagementPolicyRulesWorkflowStep> | undefined
    ): string | null => {
        if (steps) {
            const all = steps.length;
            const completed = steps.filter(
                (s) =>
                    s.status === WorkflowStepStatus.Completed ||
                    s.status === WorkflowStepStatus.Failed
            ).length;

            return `${completed} / ${all}`;
        }

        return null;
    };

    const getProgressColor = (
        steps: ReadonlyArray<ManagementPolicyRulesWorkflowStep> | undefined
    ): string | undefined => {
        if (steps) {
            const palet: Record<string, string> = {
                normal: primaryBlue,
                warning: primaryYellow,
            };

            return steps.some((s) => s.status === WorkflowStepStatus.Failed)
                ? palet["warning"]
                : palet["normal"];
        }
    };

    const getRuleDescription = (
        rule: ManagementPolicyRule
    ): React.ReactElement | string => {
        const [tierKey, tierValue]: [string, DateAfterModification] =
            Object.entries(rule.definition.actions.baseBlob).find(
                ([, value]) =>
                    value !== null && (value as DateAfterModification)
            ) as [string, DateAfterModification];
        if (!tierKey && !tierValue) {
            return t("unknown");
        }

        const tierDescription = tierDescriptions[tierKey];

        const descriptionText = template(tierDescription)({
            daysAfterModification: tierValue.daysAfterModificationGreaterThan,
        });

        return <Text dangerouslySetInnerHTML={{ __html: descriptionText }} />;
    };

    const getConfirmationMessage = (): string => {
        return template(t("storageAccounts.confirmationMessage"))({
            rules: selectedManagementPolicyRules
                .map((r) => `<b>${r.name}</b>`)
                .join(", "),
        });
    };

    const progressPercent = getCompletedStepsPrecent(
        managementPolicyRulesWorkflow?.steps
    );
    const progressStatement = getProgressStatement(
        managementPolicyRulesWorkflow?.steps
    );
    const progressColor = getProgressColor(
        managementPolicyRulesWorkflow?.steps
    );
    const inProgress =
        managementPolicyRulesWorkflow?.status === WorkflowStatus.InProgress;

    const confirmMessage = getConfirmationMessage();

    const handleOpenHistory = (
        tableItem: TableItem<Tenant<StorageAccount>>
    ) => {
        setSelectedStorageAccountTableItem(tableItem);
        setHistoryLogsOpen(true);
    };
    const handleOpenErrorLogs = () => {
        setErrorLogsOpen(true);
    };
    const handleNavigateToTenant = (tenant: Tenant<StorageAccount>) =>
        navigate(
            `/environments/${activeEnvironmentId}/tenants/${tenant.id}/general-configs`
        );

    const handleConfirmSubmission = () => {
        setConfirmationOpen(false);

        showLoader?.(true);
        Promise.all([submitManagementPolicyRules()]).then((value) => {
            showLoader?.(false);

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = t("storageAccounts.successfulAlertMessage");
            }

            toaster.push(
                <Message showIcon type={type}>
                    {message}
                </Message>,
                tostOptions
            );
        });
    };

    const handleCancelSubmission = () => {
        setConfirmationOpen(false);
    };

    const handleSubmit = () => {
        setConfirmationOpen(true);
    };

    const handleCheckManagementPolicyRule = (
        item: TableItem<ManagementPolicyRule>,
        checked: boolean
    ) => {
        setManagementPolicyRulesTableData((draft) => {
            draft.forEach((ti) => {
                if (ti.name === item.name && !item.readonly) {
                    ti.checked = checked;
                }
            });
        });
    };

    const handleCheckAllManagementPolicyRule = (checked: boolean) => {
        setManagementPolicyRulesAllChecked(checked);
        setManagementPolicyRulesTableData((draft) => {
            draft.forEach((ti) => {
                if (!ti.readonly) {
                    ti.checked = checked;
                }
            });
        });
    };

    const handleCheckStorageAccounts = (
        item: TableItem<Tenant<StorageAccount>>,
        checked: boolean
    ) => {
        setStorageAccountsTableData((draft) => {
            draft.forEach((ti) => {
                if (ti.id === item.id && !item.readonly) {
                    ti.checked = checked;
                }
            });
        });
    };

    const handleCheckAllStorageAccounts = (checked: boolean) => {
        setStorageAccountsAllChecked(checked);
        setStorageAccountsTableData((draft) => {
            draft.forEach((ti) => {
                if (!ti.readonly) {
                    ti.checked = checked;
                }
            });
        });
    };

    const handleSortColumn = (
        sortColumn: string | undefined,
        sortType: SortType | undefined
    ) => {
        setStorageAccountsSortColumn(
            sortColumn as keyof Tenant<StorageAccount>
        );
        setStorageAccountsSortType(sortType);

        setStorageAccountsTableData((draft) =>
            getSorted(
                draft,
                sortColumn as keyof Tenant<StorageAccount>,
                sortType
            )
        );
    };

    useEffect(() => {
        setSelectedStorageAccounts(
            produce(storageAccountsTableData, (draft) => {
                return draft
                    .filter((ti) => ti.checked)
                    .map((ti) => ({
                        ...ti,
                        data: {
                            ...ti.data,
                            managementPolicyRules: Array.from(
                                selectedManagementPolicyRules.map((pr) => ({
                                    ...pr,
                                    definition: {
                                        ...pr.definition,
                                        filters: {
                                            prefixMatch: Array.from(
                                                pr.definition.filters
                                                    ?.prefixMatch ?? []
                                            ),
                                            blobTypes: Array.from(
                                                pr.definition.filters
                                                    ?.blobTypes ?? []
                                            ),
                                        },
                                    },
                                }))
                            ),
                        },
                    }));
            })
        );

        setStorageAccountsAllChecked(
            storageAccountsTableData.some((ti) => ti.checked)
        );

        dispatch(
            setIsUnsavedChangeAvailable(
                storageAccountsTableData.some((ti) => ti.checked)
            )
        );
    }, [storageAccountsTableData, selectedManagementPolicyRules, dispatch]);

    useEffect(() => {
        setSelectedManagementPolicyRules(
            produce(managementPolicyRulesTableData, (draft) => {
                return draft
                    .filter((ti) => ti.checked)
                    .map((ti) => ({
                        ...ti,
                    }));
            })
        );

        setManagementPolicyRulesAllChecked(
            managementPolicyRulesTableData.some((ti) => ti.checked)
        );
    }, [managementPolicyRulesTableData, dispatch]);

    useEffect(() => {
        if (activeEnvironmentId) {
            fetchData();
        }
    }, [activeEnvironmentId]);

    useEffect(() => {
        if (!activeEnvironmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [activeEnvironmentId, dispatch]);

    useRefreshPage(() => {
        if (activeEnvironmentId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (managementPolicyRulesWorkflow) {
            addWebSocketHabName(HUB_NAMES.MANAGEMENT_POLICY_RULES_CREATION);
            addWebSocketUserId(activeEnvironmentId);
        }
    }, [activeEnvironmentId, managementPolicyRulesWorkflow]);

    useEffect(() => {
        const steps = managementPolicyRulesWorkflow?.steps;
        const status = managementPolicyRulesWorkflow?.status;

        if (
            (status === WorkflowStatus.NotStarted ||
                status === WorkflowStatus.InProgress) &&
            steps
        ) {
            if (steps?.length > 0) {
                setStorageAccountsTableData((draft) =>
                    draft.forEach((ti) => {
                        ti.readonly = steps?.some((s) => s.data.id === ti.id);
                    })
                );
            }

            dispatch(setIsUnsavedChangeAvailable(false));
        }
    }, [managementPolicyRulesWorkflow, dispatch]);

    useEffect(() => {
        if (webSocketPayload) {
            try {
                const payload = JSON.parse(
                    webSocketPayload
                ) as ManagementPolicyRulesWorkflow;
                if (payload) {
                    setManagementPolicyRulesWorkflow(payload);

                    if (payload.status === WorkflowStatus.Succeeded) {
                        if (
                            payload.steps.some(
                                (s) => s.status === WorkflowStepStatus.Failed
                            )
                        ) {
                            toaster.push(
                                <Message showIcon type={"warning"}>
                                    {t("storageAccounts.warningAlertMessage")}
                                </Message>,
                                tostOptions
                            );
                        }

                        fetchData();
                    }
                }
            } catch (error) {
                console.error("Failed to parse payload:", error);
            }
        }
    }, [webSocketPayload]);

    useInactiveEnvironment();

    const rulesTableTitle = (
        <Heading level={3}>{t(`storageAccounts.rules`)}</Heading>
    );

    return (
        dataFetched && (
            <Container className="storage-accounts__container">
                <Header className="storage-accounts__header">
                    <Heading level={1}>{title}</Heading>
                </Header>

                <Content className="storage-accounts__content">
                    <Collapsible title={rulesTableTitle}>
                        <Table
                            style={{ margin: "1rem 0" }}
                            autoHeight={true}
                            data={managementPolicyRulesTableData}
                            headerHeight={headerHeight}
                            rowHeight={rowHeight}
                        >
                            <Table.Column width={60} fixed>
                                <Table.HeaderCell>
                                    <Checkbox
                                        indeterminate={
                                            managementPolicyRulesIndeterminate
                                        }
                                        checked={
                                            managementPolicyRulesAllChecked
                                        }
                                        disabled={inProgress}
                                        readOnly={inProgress}
                                        onChange={() => {
                                            handleCheckAllManagementPolicyRule(
                                                !managementPolicyRulesAllChecked
                                            );
                                        }}
                                    />
                                </Table.HeaderCell>
                                <Table.Cell>
                                    {(
                                        rowData: TableItem<ManagementPolicyRule>
                                    ) => (
                                        <Checkbox
                                            readOnly={rowData.readonly}
                                            checked={rowData.checked}
                                            onChange={(_, checked) => {
                                                handleCheckManagementPolicyRule(
                                                    rowData,
                                                    checked
                                                );
                                            }}
                                            className={
                                                rowData.readonly
                                                    ? "storage-accounts__table__row--readonly"
                                                    : ""
                                            }
                                        />
                                    )}
                                </Table.Cell>
                            </Table.Column>

                            <Table.Column flexGrow={0.25} minWidth={150} fixed>
                                <Table.HeaderCell>
                                    {t(
                                        `storageAccounts.managementPolicyRulesTable.name`
                                    )}
                                </Table.HeaderCell>
                                <Table.Cell dataKey={"name"}>
                                    {(
                                        rowData: TableItem<ManagementPolicyRule>
                                    ) => <div> {rowData.name} </div>}
                                </Table.Cell>
                            </Table.Column>

                            <Table.Column flexGrow={1} minWidth={300} fixed>
                                <Table.HeaderCell>
                                    {t(
                                        `storageAccounts.managementPolicyRulesTable.description`
                                    )}
                                </Table.HeaderCell>
                                <Table.Cell dataKey={"name"}>
                                    {(
                                        rowData: TableItem<ManagementPolicyRule>
                                    ) => (
                                        <Text>
                                            {getRuleDescription(rowData)}
                                        </Text>
                                    )}
                                </Table.Cell>
                            </Table.Column>
                        </Table>
                    </Collapsible>

                    <HStack
                        style={{ margin: "1rem 0" }}
                        justifyContent={"space-between"}
                        spacing={10}
                    >
                        <Stack.Item alignSelf={"center"} grow={1}>
                            {inProgress && (
                                <Progress.Line
                                    status={"active"}
                                    style={{ paddingLeft: "0" }}
                                    percent={progressPercent}
                                    strokeColor={progressColor}
                                    showInfo={false}
                                />
                            )}
                        </Stack.Item>

                        <Stack.Item alignSelf={"center"} grow={0.5}>
                            {inProgress && (
                                <Text weight="regular">
                                    {progressStatement}
                                </Text>
                            )}
                        </Stack.Item>

                        <Stack.Item alignSelf={"center"}>
                            <Button
                                style={{ paddingRight: "0", paddingLeft: "0" }}
                                appearance="link"
                                color="red"
                                onClick={() => handleOpenErrorLogs()}
                            >
                                <Text color="red">
                                    {t(`storageAccounts.errorLogs`)}
                                </Text>
                            </Button>
                        </Stack.Item>
                    </HStack>

                    <ConfirmModal
                        onCancel={handleCancelSubmission}
                        onConfirm={handleConfirmSubmission}
                        open={confirmationOpen}
                        message={confirmMessage}
                    />

                    <ErrorLogs
                        open={errorLogsOpen}
                        setOpen={setErrorLogsOpen}
                    />

                    <HistoryContext.Provider
                        value={selectedStorageAccountTableItem}
                    >
                        <History
                            open={historyLogsOpen}
                            setOpen={setHistoryLogsOpen}
                        />
                    </HistoryContext.Provider>

                    <Table
                        style={{ marginBottom: "1rem" }}
                        data={storageAccountsTableData}
                        shouldUpdateScroll={true}
                        autoHeight={true}
                        virtualized={true}
                        headerHeight={headerHeight}
                        rowHeight={rowHeight}
                        sortColumn={storageAccountsSortColumn}
                        sortType={storageAccountsSortType}
                        onSortColumn={(dataKey, sortType) =>
                            handleSortColumn(dataKey, sortType)
                        }
                    >
                        <Table.Column width={60} fixed>
                            <Table.HeaderCell>
                                <Checkbox
                                    indeterminate={storageAccountsIndeterminate}
                                    checked={storageAccountsAllChecked}
                                    disabled={inProgress}
                                    readOnly={inProgress}
                                    onChange={() => {
                                        handleCheckAllStorageAccounts(
                                            !storageAccountsAllChecked
                                        );
                                    }}
                                />
                            </Table.HeaderCell>
                            <Table.Cell>
                                {(
                                    rowData: TableItem<Tenant<StorageAccount>>
                                ) => (
                                    <Checkbox
                                        readOnly={rowData.readonly}
                                        checked={rowData.checked}
                                        onChange={(_, checked) => {
                                            handleCheckStorageAccounts(
                                                rowData,
                                                checked
                                            );
                                        }}
                                        className={
                                            rowData.readonly
                                                ? "storage-accounts__table__row--readonly"
                                                : ""
                                        }
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column
                            flexGrow={0.5}
                            minWidth={150}
                            fixed
                            sortable
                        >
                            <Table.HeaderCell>
                                {t(`storageAccounts.storageAccountsTable.name`)}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={"data.name"}>
                                {(rowData: Tenant<StorageAccount>) => (
                                    <div> {rowData.data.name} </div>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.5} minWidth={100} sortable>
                            <Table.HeaderCell>
                                {t(
                                    `storageAccounts.storageAccountsTable.tenant`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={"name"}>
                                {(rowData: Tenant<StorageAccount>) => (
                                    <Button
                                        style={{
                                            paddingRight: "0",
                                            paddingLeft: "0",
                                        }}
                                        appearance="link"
                                        onClick={() =>
                                            handleNavigateToTenant(rowData)
                                        }
                                    >
                                        <Text color={"blue"}>
                                            {rowData.name}
                                        </Text>
                                    </Button>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.2} minWidth={100} sortable>
                            <Table.HeaderCell>
                                {t(
                                    `storageAccounts.storageAccountsTable.moniker`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={"moniker"}>
                                {(rowData: Tenant<StorageAccount>) => (
                                    <Text> {rowData.moniker} </Text>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.3} minWidth={120} sortable>
                            <Table.HeaderCell>
                                {t(
                                    `storageAccounts.storageAccountsTable.createdOn`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell dataKey="data.createdOn">
                                {(rowData: Tenant<StorageAccount>) => (
                                    <Text>
                                        {formatDateTimeShort(
                                            new Date(rowData.data.createdOn)
                                        )}
                                    </Text>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.2} minWidth={100}>
                            <Table.HeaderCell>
                                <Text>
                                    {t(
                                        `storageAccounts.storageAccountsTable.cool.name`
                                    )}

                                    <Whisper
                                        placement="topStart"
                                        trigger="hover"
                                        speaker={
                                            <Tooltip>
                                                {t(
                                                    `storageAccounts.storageAccountsTable.cool.helpText`
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <HelpOutlineIcon
                                            style={{
                                                marginLeft: "0.25rem",
                                                marginBottom: "0.125rem",
                                            }}
                                        />
                                    </Whisper>
                                </Text>
                            </Table.HeaderCell>
                            <Table.Cell dataKey="data.managementPolicyRules">
                                {(rowData: Tenant<StorageAccount>) => (
                                    <Text>
                                        {getAppliedRuleValue(
                                            rowData.data.managementPolicyRules,
                                            ruleNames.cool
                                        )}
                                    </Text>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.2} minWidth={100}>
                            <Table.HeaderCell>
                                <Text>
                                    {t(
                                        `storageAccounts.storageAccountsTable.cold.name`
                                    )}
                                    <Whisper
                                        placement="topStart"
                                        trigger="hover"
                                        speaker={
                                            <Tooltip>
                                                {t(
                                                    `storageAccounts.storageAccountsTable.cold.helpText`
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <HelpOutlineIcon
                                            style={{
                                                marginLeft: "0.25rem",
                                                marginBottom: "0.125rem",
                                            }}
                                        />
                                    </Whisper>
                                </Text>
                            </Table.HeaderCell>
                            <Table.Cell dataKey="data.managementPolicyRules">
                                {(rowData: Tenant<StorageAccount>) => (
                                    <Text>
                                        {getAppliedRuleValue(
                                            rowData.data.managementPolicyRules,
                                            ruleNames.cold
                                        )}
                                    </Text>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.2} minWidth={100}>
                            <Table.HeaderCell>
                                <Text>
                                    {t(
                                        `storageAccounts.storageAccountsTable.archive.name`
                                    )}
                                    <Whisper
                                        placement="topStart"
                                        trigger="hover"
                                        speaker={
                                            <Tooltip>
                                                {t(
                                                    `storageAccounts.storageAccountsTable.archive.helpText`
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <HelpOutlineIcon
                                            style={{
                                                marginLeft: "0.25rem",
                                                marginBottom: "0.125rem",
                                            }}
                                        />
                                    </Whisper>
                                </Text>
                            </Table.HeaderCell>
                            <Table.Cell dataKey="data.managementPolicyRules">
                                {(rowData: Tenant<StorageAccount>) => (
                                    <Text>
                                        {getAppliedRuleValue(
                                            rowData.data.managementPolicyRules,
                                            ruleNames.archive
                                        )}
                                    </Text>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.2} minWidth={100}>
                            <Table.HeaderCell>
                                <Text>
                                    {t(
                                        `storageAccounts.storageAccountsTable.delete.name`
                                    )}
                                    <Whisper
                                        placement="topStart"
                                        trigger="hover"
                                        speaker={
                                            <Tooltip>
                                                {t(
                                                    `storageAccounts.storageAccountsTable.delete.helpText`
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <HelpOutlineIcon
                                            style={{
                                                marginLeft: "0.25rem",
                                                marginBottom: "0.125rem",
                                            }}
                                        />
                                    </Whisper>
                                </Text>
                            </Table.HeaderCell>
                            <Table.Cell dataKey="data.managementPolicyRules">
                                {(rowData: Tenant<StorageAccount>) => (
                                    <Text>
                                        {getAppliedRuleValue(
                                            rowData.data.managementPolicyRules,
                                            ruleNames.delete
                                        )}
                                    </Text>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column flexGrow={0.175} minWidth={100}>
                            <Table.HeaderCell>
                                {t(
                                    `storageAccounts.storageAccountsTable.history`
                                )}
                            </Table.HeaderCell>
                            <Table.Cell>
                                {(
                                    rowData: TableItem<Tenant<StorageAccount>>
                                ) => (
                                    <IconButton
                                        appearance="subtle"
                                        size="sm"
                                        onClick={() => {
                                            handleOpenHistory(rowData);
                                        }}
                                        icon={<HistoryIcon />}
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>
                    </Table>
                </Content>

                <RsuiteFooter>
                    <Footer onSubmit={handleSubmit} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(
    withLoader,
    withError,
    withWebSocket
)(StorageAccounts);

export default Component;
